import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import {
  Alert,
  Breadcrumb,
  BreadcrumbItem,
  Content,
  Flex,
  FlexItem,
  Icon,
  Modal,
  ModalBody,
  ModalHeader,
  PageBreadcrumb,
  PageSection,
  PageSectionVariants,
  Radio,
  Spinner,
  Title,
  Wizard,
  WizardStep,
} from '@patternfly/react-core';
import { DomainIcon } from '@patternfly/react-icons';
import Unauthenticated from '@app/Unauthenticated';
import { useNotifications } from '../../../../contexts/NotificationContext';
import { useUserContext } from '../../../../contexts/user/UserContext';
import type { PaymentSource } from '../../../../types/models/Payment';
import { useDomainTransferability } from '../../../../hooks/useDomainTransferability';
import { useDomainNames } from '../../../../hooks/useDomainNames';
import { useDomainContact } from '../../../../hooks/useDomainContact';
import { useCreateDomainOrder } from '../../../../hooks/useCreateDomainOrder';
import { useDomainOrderPreview } from '../../../../hooks/useDomainOrderPreview';
import type { DomainTransferOrderCreateRequest } from '../../../../types/models';
import ContactStep from '../Register/ContactStep';
import { RecapFooter } from '../Register/RecapStep';
import { EMPTY_CONTACT, buildContactRequest, isContactDraftValid } from '../Register/types';
import type { ContactDraft, ContactMode } from '../Register/types';
import TransferDomainsStep from './TransferDomainsStep';
import TransferRecapStep from './TransferRecapStep';
import { MAX_TRANSFER_CART, TRANSFER_STEP_IDS, normalizeDomainInput } from './types';
import type { TransferCartItem } from './types';

const DomainTransfer: React.FunctionComponent = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const { addNotification, setDrawerOpen } = useNotifications();

  // --- Read hooks (kept at the page level so their state survives step navigation) ---
  const { results: transferabilityResults, loading: checking, error: checkError, check } = useDomainTransferability();
  const { domainNames, loading: domainNamesLoading } = useDomainNames();
  const { create, loading: creating } = useCreateDomainOrder();
  const { preview, loading: previewLoading, error: previewError, fetchPreview } = useDomainOrderPreview();

  // --- Wizard state (lifted above <Wizard>) ---
  // Step 1 — domains & auth codes
  const [domainInput, setDomainInput] = useState('');
  const [authCode, setAuthCode] = useState('');
  const [cart, setCart] = useState<TransferCartItem[]>([]);
  // Per-name create-time errors (keyed by FQDN) surfaced on the cart rows after a failed submit.
  const [itemErrors, setItemErrors] = useState<Record<string, string>>({});

  // Step 2 — contact. `keepContact` = plain transfer preserving the domain's existing registrant
  // (no contact captured — for some registries, .be notably, a registrant change during a transfer
  // is a separate legal operation, a "trade"). Default false = today's behavior (provide a contact).
  const [keepContact, setKeepContact] = useState(false);
  const [contactMode, setContactMode] = useState<ContactMode>('new');
  const [contact, setContact] = useState<ContactDraft>(EMPTY_CONTACT);
  const [contactErrors, setContactErrors] = useState<Record<string, string>>({});
  const [selectedPortfolioId, setSelectedPortfolioId] = useState<number>(0);
  const { contact: portfolioContact, loading: portfolioContactLoading } = useDomainContact(selectedPortfolioId);

  // Step 3 — recap
  const [agreementsAccepted, setAgreementsAccepted] = useState(false);
  // Payment source — prepaid balance / saved card / hosted checkout (default). Ignored for a real
  // staff creator (the backend waives the payment), so the payload omits `payment` for staff.
  const [paymentSource, setPaymentSource] = useState<PaymentSource>('checkout');
  const [saveCard, setSaveCard] = useState(false);
  const [paying, setPaying] = useState(false);
  const user = useUserContext();
  const isStaff = user?.is_ancalagon_staff === true;

  // Apply a portfolio selection to the contact form EXACTLY ONCE per selection (identical to the
  // registration wizard — keying on the applied id, not the reference, so a background OIDC silent
  // renew's refetch can't clobber later edits; and only once the fetch for THIS id has settled).
  const appliedPortfolioId = useRef<number>(0);
  useEffect(() => {
    if (selectedPortfolioId <= 0 || portfolioContactLoading) {
      return;
    }
    const settledForThisId = portfolioContact === null || portfolioContact.domain_name_id === selectedPortfolioId;
    if (!settledForThisId || appliedPortfolioId.current === selectedPortfolioId) {
      return;
    }
    appliedPortfolioId.current = selectedPortfolioId;
    if (portfolioContact) {
      setContact({
        first_name: portfolioContact.first_name ?? '',
        last_name: portfolioContact.last_name ?? '',
        organization: portfolioContact.organization ?? '',
        email: portfolioContact.email ?? '',
        phone: portfolioContact.phone ?? '',
        address_line1: portfolioContact.address_line1 ?? '',
        address_line2: portfolioContact.address_line2 ?? '',
        city: portfolioContact.city ?? '',
        state: portfolioContact.state ?? '',
        zipcode: portfolioContact.zipcode ?? '',
        countrycode: portfolioContact.countrycode ?? '',
      });
    } else {
      setContact(EMPTY_CONTACT);
    }
    setContactErrors({});
  }, [selectedPortfolioId, portfolioContact, portfolioContactLoading]);

  const handleCheck = useCallback(
    (domain: string) => {
      void check([normalizeDomainInput(domain)]);
    },
    [check],
  );

  const addToCart = useCallback((item: TransferCartItem) => {
    setCart((prev) => {
      if (prev.length >= MAX_TRANSFER_CART || prev.some((c) => c.domain === item.domain)) {
        return prev;
      }
      return [...prev, item];
    });
    // Clear the add-row so the verdict panel (which keys off the current input) collapses.
    setDomainInput('');
    setAuthCode('');
  }, []);

  const removeFromCart = useCallback((domain: string) => {
    setCart((prev) => prev.filter((c) => c.domain !== domain));
    setItemErrors((prev) => {
      if (!(domain in prev)) {
        return prev;
      }
      const next = { ...prev };
      delete next[domain];
      return next;
    });
  }, []);

  const onContactFieldChange = useCallback((field: keyof ContactDraft, value: string) => {
    setContact((prev) => ({ ...prev, [field]: value }));
    setContactErrors((prev) => {
      if (!prev[field]) {
        return prev;
      }
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const portfolioContactMissing = selectedPortfolioId > 0 && !portfolioContactLoading && portfolioContact === null;

  // --- Per-step validity (gates the footer's Next / Confirm) ---
  const cartValid = cart.length > 0;
  // A keep-contact transfer captures no contact at all, so the form's validity is irrelevant.
  const contactValid = keepContact || isContactDraftValid(contact);
  const hasUnsupported = preview ? preview.items.some((i) => !i.supported) : false;
  const canSubmit = cartValid && contactValid && agreementsAccepted && !hasUnsupported && !creating;

  const handleConfirm = useCallback(async (): Promise<{ jumpToStepId?: string }> => {
    // Clear per-row errors from any previous attempt so a resubmit starts clean (a since-resolved
    // conflict shouldn't keep flagging its row).
    setItemErrors({});

    // keep_contact: true and contact are mutually exclusive — the backend rejects a body carrying
    // both, so the keep branch must not send a contact key at all.
    const payload: DomainTransferOrderCreateRequest = {
      type: 'transfer',
      items: cart.map((c) => ({ name: c.domain, auth_code: c.authCode })),
      ...(keepContact ? { keep_contact: true } : { contact: buildContactRequest(contact) }),
      agreements_accepted: true,
      ...(isStaff
        ? {}
        : {
            payment: {
              source: paymentSource,
              ...(paymentSource === 'checkout' && saveCard ? { save_card: true } : {}),
            },
          }),
    };

    const outcome = await create(payload);

    if (outcome.status === 'success') {
      if (outcome.data.payment_waived) {
        // Staff order — the backend waived the Mollie payment (there is no checkout to redirect
        // to): the order is already `paid` and awaits the fulfilment worker; an unpaid invoice
        // will be issued to the client. Land on the shared order status page.
        addNotification({
          title: 'Transfer order created — payment waived (staff)',
          srTitle: 'Success alert',
          description:
            'No online payment is needed for a staff order. The transfer will start automatically and an unpaid invoice will be issued to the client.',
          variant: 'success',
        });
        setDrawerOpen(true);
        navigate(`/domain-orders/${outcome.data.order_id}/return`);
        return {};
      }
      if (outcome.data.checkout_url === null) {
        // Paid without a redirect: the prepaid balance was debited inside the order transaction
        // (already `paid`), or the saved card was charged in the background (`paid` once Mollie
        // settled it, else the return page's poll sees it flip). Land on the shared status page.
        if (outcome.data.payment_source === 'wallet') {
          addNotification({
            title: 'Order paid from your prepaid balance',
            srTitle: 'Success alert',
            description: 'Your balance was debited and the transfer starts automatically.',
            variant: 'success',
          });
        } else if (outcome.data.paid) {
          addNotification({
            title: 'Order paid with your saved card',
            srTitle: 'Success alert',
            description: 'Your saved card was charged and the transfer starts automatically.',
            variant: 'success',
          });
        } else {
          addNotification({
            title: 'Charging your saved card',
            srTitle: 'Info alert',
            description: "Your saved card is being charged — we'll confirm the payment in a moment.",
            variant: 'info',
          });
        }
        setDrawerOpen(true);
        navigate(`/domain-orders/${outcome.data.order_id}/return`);
        return {};
      }
      // Mirror the invoice Pay flow: show the non-dismissible please-wait modal, then hand off to
      // Mollie. Deliberately leave `paying` true — the page is unloading.
      setPaying(true);
      window.location.href = outcome.data.checkout_url;
      return {};
    }

    const fe = outcome.fieldErrors;
    if (fe.contact && typeof fe.contact === 'object') {
      setContactErrors(fe.contact);
    }
    // The transfer per-name error is either a `{name: code}` map (flag those cart rows) or a flat
    // string (no single row to attach it to — surface it in the notification instead).
    if (fe.items && typeof fe.items === 'object') {
      setItemErrors(fe.items);
    }
    const itemsMessage = typeof fe.items === 'string' ? fe.items : null;
    addNotification({
      title: 'Could not start your transfer',
      srTitle: 'Error alert',
      description: itemsMessage ?? fe.payment ?? outcome.message,
      variant: 'danger',
    });
    setDrawerOpen(true);

    let jumpToStepId: string | undefined;
    if (fe.items || fe.nameservers) {
      jumpToStepId = TRANSFER_STEP_IDS.domains;
    } else if (fe.contact || fe.keep_contact) {
      jumpToStepId = TRANSFER_STEP_IDS.contact;
    } else if (fe.payment) {
      jumpToStepId = TRANSFER_STEP_IDS.recap;
    }
    return { jumpToStepId };
  }, [cart, contact, keepContact, create, addNotification, setDrawerOpen, navigate, isStaff, paymentSource, saveCard]);

  if (!auth.isAuthenticated) {
    return <Unauthenticated />;
  }

  return (
    <>
      <PageBreadcrumb hasBodyWrapper={false} hasShadowBottom={false}>
        <Breadcrumb ouiaId="DomainTransferBreadcrumb">
          <BreadcrumbItem to="/datacenter">Datacenter</BreadcrumbItem>
          <BreadcrumbItem to="/datacenter/domains">Domain Names</BreadcrumbItem>
          <BreadcrumbItem isActive>Transfer a domain</BreadcrumbItem>
        </Breadcrumb>
      </PageBreadcrumb>
      <PageSection hasBodyWrapper variant={PageSectionVariants.default} isWidthLimited hasShadowBottom={true}>
        <Content component="h1">
          <Icon size="lg" style={{ marginRight: '8px' }}>
            <DomainIcon />
          </Icon>
          Transfer a domain to us
        </Content>
        <Content component="p">
          Bring a domain you own elsewhere to Ancalagon: check each domain with its transfer code, assign a registrant
          contact, then pay to start the transfer.
        </Content>
      </PageSection>
      <PageSection hasBodyWrapper={false}>
        <Wizard navAriaLabel="Domain transfer steps" onClose={() => navigate('/datacenter/domains')}>
          <WizardStep name="Domains & codes" id={TRANSFER_STEP_IDS.domains} footer={{ isNextDisabled: !cartValid }}>
            <TransferDomainsStep
              domainInput={domainInput}
              onDomainInputChange={setDomainInput}
              authCode={authCode}
              onAuthCodeChange={setAuthCode}
              results={transferabilityResults}
              checking={checking}
              checkError={checkError}
              onCheck={handleCheck}
              cart={cart}
              onAddToCart={addToCart}
              onRemoveFromCart={removeFromCart}
              itemErrors={itemErrors}
            />
          </WizardStep>

          <WizardStep name="Contact" id={TRANSFER_STEP_IDS.contact} footer={{ isNextDisabled: !contactValid }}>
            <Flex direction={{ default: 'column' }} spaceItems={{ default: 'spaceItemsMd' }}>
              <FlexItem>
                <Title headingLevel="h2" size="lg">
                  Registrant contact
                </Title>
                <Content component="p">
                  Choose what happens to the owner (registrant) contact of the domains during the transfer.
                </Content>
              </FlexItem>
              <FlexItem>
                <div role="radiogroup" aria-label="Registrant contact during the transfer">
                  <Radio
                    id="transfer-contact-keep"
                    name="transfer-contact-choice"
                    label="Keep the domain's current registrant contact (recommended for .be)"
                    description={
                      'The domains are transferred as-is: the registrant stays whoever it is today. For some ' +
                      'registries (.be notably), changing the registrant during a transfer is a separate legal ' +
                      'operation (a “trade”) — this option requests a plain transfer instead.'
                    }
                    isChecked={keepContact}
                    onChange={() => setKeepContact(true)}
                  />
                  <Radio
                    id="transfer-contact-new"
                    name="transfer-contact-choice"
                    className="pf-v6-u-mt-sm"
                    label="Provide a new registrant contact"
                    description="The contact you enter below becomes the registrant of every domain in this transfer."
                    isChecked={!keepContact}
                    onChange={() => setKeepContact(false)}
                  />
                </div>
              </FlexItem>
              {keepContact ? (
                <FlexItem>
                  <Alert variant="info" isInline title="No contact to fill in">
                    The current registrant contact stays in place at the registry. It will appear in the Manager
                    automatically once the transfer completes — we import it from the registrar.
                  </Alert>
                </FlexItem>
              ) : (
                <FlexItem>
                  <ContactStep
                    hideIntro
                    mode={contactMode}
                    onModeChange={setContactMode}
                    contact={contact}
                    onFieldChange={onContactFieldChange}
                    errors={contactErrors}
                    domainNames={domainNames}
                    domainNamesLoading={domainNamesLoading}
                    selectedPortfolioId={selectedPortfolioId}
                    onSelectPortfolio={setSelectedPortfolioId}
                    portfolioContactLoading={portfolioContactLoading}
                    portfolioContactMissing={portfolioContactMissing}
                  />
                </FlexItem>
              )}
            </Flex>
          </WizardStep>

          <WizardStep
            name="Review & pay"
            id={TRANSFER_STEP_IDS.recap}
            footer={<RecapFooter canSubmit={canSubmit} submitting={creating || paying} onConfirm={handleConfirm} />}
          >
            <TransferRecapStep
              cart={cart}
              contact={contact}
              keepContact={keepContact}
              preview={preview}
              previewLoading={previewLoading}
              previewError={previewError}
              onRequestPreview={fetchPreview}
              agreementsAccepted={agreementsAccepted}
              onAgreementsChange={setAgreementsAccepted}
              paymentSource={paymentSource}
              onPaymentSourceChange={setPaymentSource}
              saveCard={saveCard}
              onSaveCardChange={setSaveCard}
            />
          </WizardStep>
        </Wizard>
      </PageSection>

      {/* Mirrors the invoice/registration Pay flow: a non-dismissible please-wait modal bridging the
          click to the Mollie redirect. disableFocusTrap is load-bearing — this modal has no focusable
          element, and the focus-trap throws at render with nothing to focus. */}
      <Modal variant="small" isOpen={paying} aria-label="Generating payment" disableFocusTrap>
        <ModalHeader title="Generating your payment" />
        <ModalBody>
          <Flex
            direction={{ default: 'column' }}
            alignItems={{ default: 'alignItemsCenter' }}
            spaceItems={{ default: 'spaceItemsMd' }}
          >
            <FlexItem>
              <Spinner size="xl" aria-label="Generating payment" />
            </FlexItem>
            <FlexItem>
              <Content component="p" className="pf-v6-u-text-align-center">
                Preparing your secure payment. You will be redirected to our payment provider in a moment — please keep
                this window open.
              </Content>
            </FlexItem>
          </Flex>
        </ModalBody>
      </Modal>
    </>
  );
};

export default DomainTransfer;
